import React from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, AlertCircle, Scale, UserX, Shield } from 'lucide-react';
export function TermsOfServicePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1553" data-mp="1htqk3h47c10n">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1554" data-mp="1hquinit5au7g">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1555" data-mp="1cs9mbuh0kuoa">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1556" data-mp="ywfllyqe9bm9">
            <span className="w-8 h-px bg-teal" data-id="element-1557" data-mp="u2l2oxim50o8"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1558" data-mp="6e1wcnj4ixys">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1559" data-mp="1fqmicstwngk"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1560" data-mp="g6n6dy0jxdki">
            Terms of Service
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-1561" data-mp="k6pasexo8l3h">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1562" data-mp="3tf9s11u9jze6">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1563" data-mp="1a5w7a43m4zj">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1564" data-mp="1hn4jwda6z23h">
            These Terms of Service ("Terms") govern your access to and use of
            SyNeural's platform, services, and website (collectively, the
            "Services"). By accessing or using our Services, you agree to be
            bound by these Terms.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1565" data-mp="16whmw0e512op">
            If you do not agree to these Terms, you may not access or use our
            Services. If you are using our Services on behalf of an
            organization, you represent that you have the authority to bind that
            organization to these Terms.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-1566" data-mp="ny5c6a7kvkr0">
        {/* Acceptance of Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1567" data-mp="j6ezhxl64kwf">
          <div className="flex items-center gap-4 mb-6" data-id="element-1568" data-mp="224a7oi0zpua2">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1569" data-mp="26x3b92kq2wkk">
              <CheckCircle size={24} data-id="element-1570" data-mp="1s8gju2htmecz" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1571" data-mp="1s5m24iq6z7n9">
              Acceptance of Terms
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-1572" data-mp="2kw2yq6wi15f5">
            By creating an account, accessing our platform, or using any of our
            Services, you acknowledge that you have read, understood, and agree
            to be bound by these Terms, as well as our Privacy Policy. These
            Terms constitute a legally binding agreement between you and
            SyNeural.
          </p>
        </motion.div>

        {/* Eligibility */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1573" data-mp="10ilui5tyqoob">
          <div className="flex items-center gap-4 mb-6" data-id="element-1574" data-mp="1fwpih4abxavk">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1575" data-mp="1b07kqodf2csq">
              <UserX size={24} data-id="element-1576" data-mp="161nyemuwuyzu" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1577" data-mp="23nuzhms0a97g">
              Eligibility
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1578" data-mp="1yq1wt3pwvxbx">
            To use our Services, you must:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1579" data-mp="1tocinf7e73jm">
            <li data-id="element-1580" data-mp="1pnai8v1qolg2">
              Be at least 13 years of age (or the age of majority in your
              jurisdiction)
            </li>
            <li data-id="element-1581" data-mp="i8nsyg2r7qf4">Have the legal capacity to enter into a binding agreement</li>
            <li data-id="element-1582" data-mp="e7ltby4xdccm">
              Not be prohibited from using our Services under applicable law
            </li>
            <li data-id="element-1583" data-mp="2pyd4q6v0cpi3">Provide accurate and complete registration information</li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1584" data-mp="2tyn9jg72d9vd">
            For users under 18, access through educational institutions or with
            parental consent may be required.
          </p>
        </motion.div>

        {/* Account Registration and Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1585" data-mp="38ntb3atdjc6f">
          <div className="flex items-center gap-4 mb-6" data-id="element-1586" data-mp="3mlht8aw2457s">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1587" data-mp="1n9ovud5cwxuj">
              <Shield size={24} data-id="element-1588" data-mp="1s88fu80e3zk9" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1589" data-mp="1s90ddodjs7d6">
              Account Registration and Security
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1590" data-mp="2ql4di1rjqubx">
            <p data-id="element-1591" data-mp="34kccatjss0fl">When you create an account, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1592" data-mp="34lx6uj643jdg">
              <li data-id="element-1593" data-mp="2zkra8a9bzbyn">Provide accurate, current, and complete information</li>
              <li data-id="element-1594" data-mp="3eijpd1aytycb">Maintain and update your information to keep it accurate</li>
              <li data-id="element-1595" data-mp="3egfbbqjcriyb">
                Maintain the security and confidentiality of your account
                credentials
              </li>
              <li data-id="element-1596" data-mp="3nbd6z5dvx9tc">
                Notify us immediately of any unauthorized access or security
                breach
              </li>
              <li data-id="element-1597" data-mp="3j9rz5yg7jlq0">
                Accept responsibility for all activities that occur under your
                account
              </li>
            </ul>
            <p data-id="element-1598" data-mp="3sageqxl91u50">
              You may not share your account credentials, create multiple
              accounts, or allow others to use your account. SyNeural reserves
              the right to suspend or terminate accounts that violate these
              requirements.
            </p>
          </div>
        </motion.div>

        {/* Use of Services */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1599" data-mp="5u90dvs8cr5o">
          <div className="flex items-center gap-4 mb-6" data-id="element-1600" data-mp="1se1exxrupv2">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1601" data-mp="3j8dqqlcuwd4y">
              <FileText size={24} data-id="element-1602" data-mp="3o6nmax2htfr3" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1603" data-mp="3a6moxrlfsqgd">
              Permitted Use
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1604" data-mp="3e6oex3ht7vft">
            You may use our Services for lawful purposes in accordance with
            these Terms. You agree not to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1605" data-mp="3ppfgbqcj2fih">
            <li data-id="element-1606" data-mp="3koi2fpyjw7ms">
              Violate any applicable laws, regulations, or third-party rights
            </li>
            <li data-id="element-1607" data-mp="3fp6jcaapoq21">
              Use the Services for any fraudulent, harmful, or malicious purpose
            </li>
            <li data-id="element-1608" data-mp="3bpefuc06da3j">
              Attempt to gain unauthorized access to our systems or other users'
              accounts
            </li>
            <li data-id="element-1609" data-mp="36w1xukq041jr">
              Interfere with or disrupt the integrity or performance of the
              Services
            </li>
            <li data-id="element-1610" data-mp="2x0i1jx7iz0uh">
              Reverse engineer, decompile, or attempt to extract source code
            </li>
            <li data-id="element-1611" data-mp="2rzklpr9zmiw5">
              Use automated tools (bots, scrapers) without our written
              permission
            </li>
            <li data-id="element-1612" data-mp="2nztwhs040atq">Misrepresent your identity or affiliation</li>
            <li data-id="element-1613" data-mp="13hrkdao22vvb">
              Share, sell, or redistribute assessment content or results without
              authorization
            </li>
            <li data-id="element-1614" data-mp="17ko63a8w783h">Use the Services to harass, abuse, or harm others</li>
          </ul>
        </motion.div>

        {/* Intellectual Property */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1615" data-mp="1hhao7r9u3l1t">
          <div className="flex items-center gap-4 mb-6" data-id="element-1616" data-mp="tqwxc42zjs8t">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1617" data-mp="tpdbzz2h2r06">
              <Scale size={24} data-id="element-1618" data-mp="12ivtecoudsi" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1619" data-mp="11pvow88imjc">
              Intellectual Property Rights
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1620" data-mp="39hsl91tzae3">
            <p data-id="element-1621" data-mp="3984jr0cjdb1">
              All content, features, functionality, and materials available
              through our Services, including but not limited to text, graphics,
              logos, assessment frameworks, algorithms, software, and data
              compilations, are owned by SyNeural or its licensors and are
              protected by copyright, trademark, patent, and other intellectual
              property laws.
            </p>
            <p data-id="element-1622" data-mp="3lmxtgpw8d5mi">
              We grant you a limited, non-exclusive, non-transferable, revocable
              license to access and use our Services for your personal or
              internal organizational use. This license does not include any
              right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1623" data-mp="2xy4qwal3rzix">
              <li data-id="element-1624" data-mp="2xvse0bu7cfcq">
                Reproduce, distribute, or create derivative works from our
                content
              </li>
              <li data-id="element-1625" data-mp="1k2d83zuhmz9z">
                Use our trademarks, logos, or branding without written
                permission
              </li>
              <li data-id="element-1626" data-mp="1g1kwx1222kb2">
                Remove or alter any copyright, trademark, or proprietary notices
              </li>
              <li data-id="element-1627" data-mp="1azc4c5t3hk9h">Commercially exploit our Services or content</li>
            </ul>
          </div>
        </motion.div>

        {/* User Content and Data */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1628" data-mp="1138zutart9jd">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1629" data-mp="ncnff05o4rjd">
            User Content and Data
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1630" data-mp="ibpwumgqxvhc">
            <p data-id="element-1631" data-mp="deg2csei2tmn">
              You retain ownership of any content, data, or information you
              provide through our Services ("User Content"). By providing User
              Content, you grant SyNeural a worldwide, non-exclusive,
              royalty-free license to use, process, analyze, and store such
              content to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1632" data-mp="3uw3urtsxektm">
              <li data-id="element-1633" data-mp="3uwdj7cybojx7">Provide and improve our Services</li>
              <li data-id="element-1634" data-mp="mg0jzf6iog12">Generate assessment results and recommendations</li>
              <li data-id="element-1635" data-mp="rfdhquyxf7if">
                Conduct research and platform development (using anonymized
                data)
              </li>
              <li data-id="element-1636" data-mp="dortsrum87do">Comply with legal obligations</li>
            </ul>
            <p data-id="element-1637" data-mp="hpk47of84ibj">
              You represent and warrant that you have all necessary rights to
              provide User Content and that such content does not violate any
              third-party rights or applicable laws.
            </p>
          </div>
        </motion.div>

        {/* Payment and Subscriptions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1638" data-mp="2uvs0oref5k99">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1639" data-mp="2zo1j7md7fi1i">
            Payment and Subscriptions
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1640" data-mp="2bxo22qw5r3lp">
            <p data-id="element-1641" data-mp="26ze3e6b85kvc">
              Certain features of our Services may require payment. If you
              purchase a subscription or paid service:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1642" data-mp="2gwiijm6nkg2n">
              <li data-id="element-1643" data-mp="2gv7f5nh4dac2">
                You agree to pay all applicable fees as described at the time of
                purchase
              </li>
              <li data-id="element-1644" data-mp="3sc74qbc31b2e">
                Fees are non-refundable except as required by law or as
                specified in our refund policy
              </li>
              <li data-id="element-1645" data-mp="21weqalwi7gh3">
                Subscriptions automatically renew unless canceled before the
                renewal date
              </li>
              <li data-id="element-1646" data-mp="2auqnx7vsfej1">
                We reserve the right to change pricing with advance notice
              </li>
              <li data-id="element-1647" data-mp="2fulbp2k9nnzv">
                You are responsible for all taxes associated with your purchase
              </li>
            </ul>
            <p data-id="element-1648" data-mp="2kt63cdlgczcp">
              Failure to pay fees may result in suspension or termination of
              your access to paid Services.
            </p>
          </div>
        </motion.div>

        {/* Disclaimers and Limitations */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1649" data-mp="2ykt9r693zo97">
          <div className="flex items-center gap-4 mb-6" data-id="element-1650" data-mp="3imnrmu7rlrls">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1651" data-mp="3io8qux50xypz">
              <AlertCircle size={24} data-id="element-1652" data-mp="3dpxf2gv65e54" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1653" data-mp="38nzsbcnk817f">
              Disclaimers and Limitations of Liability
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1654" data-mp="581a3mfc2hlg">
            <p className="font-semibold" data-id="element-1655" data-mp="579cy7638bt2">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
            </p>
            <p data-id="element-1656" data-mp="3rlstoaijc32p">
              SyNeural disclaims all warranties, including but not limited to
              warranties of merchantability, fitness for a particular purpose,
              non-infringement, and accuracy. We do not warrant that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1657" data-mp="j7k5den1pygc">
              <li data-id="element-1658" data-mp="2dfo0i9cli5mt">The Services will be uninterrupted, secure, or error-free</li>
              <li data-id="element-1659" data-mp="2dhqzhivq793o">
                Assessment results will be accurate, complete, or suitable for
                any particular purpose
              </li>
              <li data-id="element-1660" data-mp="23qmi6cikvc5o">Any defects or errors will be corrected</li>
              <li data-id="element-1661" data-mp="28oxwazfwo8tp">The Services will meet your specific requirements</li>
            </ul>
            <p className="font-semibold mt-6" data-id="element-1662" data-mp="28ml47tgfoi2x">LIMITATION OF LIABILITY:</p>
            <p data-id="element-1663" data-mp="1unlhirched9e">
              To the maximum extent permitted by law, SyNeural shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages, including but not limited to loss of profits,
              data, or opportunities, arising out of or related to your use of
              the Services, even if we have been advised of the possibility of
              such damages.
            </p>
            <p data-id="element-1664" data-mp="3k3ulqag0501l">
              Our total liability for any claims related to the Services shall
              not exceed the amount you paid us in the twelve months preceding
              the claim.
            </p>
          </div>
        </motion.div>

        {/* Indemnification */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1665" data-mp="1i6ke9yjrsidi">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1666" data-mp="1n5xbnd89p7uc">
            Indemnification
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1667" data-mp="215omj53hm844">
            You agree to indemnify, defend, and hold harmless SyNeural, its
            affiliates, officers, directors, employees, and agents from any
            claims, liabilities, damages, losses, costs, or expenses (including
            reasonable attorneys' fees) arising out of or related to: (a) your
            use of the Services; (b) your violation of these Terms; (c) your
            violation of any third-party rights; or (d) your User Content.
          </p>
        </motion.div>

        {/* Termination */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1668" data-mp="3ltsvj32nskzk">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1669" data-mp="2y3ykmr1wh6hy">
            Termination
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1670" data-mp="3331q2clsleqg">
            <p data-id="element-1671" data-mp="383h2n1y92i7d">
              You may terminate your account at any time by contacting us or
              using account settings. We may suspend or terminate your access to
              the Services at any time, with or without cause or notice,
              including if:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1672" data-mp="27uhmjkqfyjw8">
              <li data-id="element-1673" data-mp="27wu2jscn2k6n">You violate these Terms</li>
              <li data-id="element-1674" data-mp="22v4tgi8z8gl7">Your use poses a security or legal risk</li>
              <li data-id="element-1675" data-mp="22y0p1zxcqt6h">You fail to pay applicable fees</li>
              <li data-id="element-1676" data-mp="1y3wi3go36l5e">We discontinue the Services</li>
            </ul>
            <p data-id="element-1677" data-mp="1t64nufzyymy1">
              Upon termination, your right to use the Services will immediately
              cease. Provisions that by their nature should survive termination
              (including intellectual property rights, disclaimers, and
              limitations of liability) will remain in effect.
            </p>
          </div>
        </motion.div>

        {/* Governing Law and Dispute Resolution */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1678" data-mp="1a7seou925d2g">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1679" data-mp="8nfvr90teg0">
            Governing Law and Dispute Resolution
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1680" data-mp="3ri2xzlclh5ff">
            These Terms shall be governed by and construed in accordance with
            the laws of [Jurisdiction], without regard to its conflict of law
            provisions. Any disputes arising out of or related to these Terms or
            the Services shall be resolved through binding arbitration in
            accordance with [Arbitration Rules], except that either party may
            seek injunctive relief in court. You waive any right to participate
            in class actions or class arbitrations.
          </p>
        </motion.div>

        {/* Changes to Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1681" data-mp="11zarj5qock96">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1682" data-mp="19hidrw4w4605">
            Changes to These Terms
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1683" data-mp="1ea1yre5wq3ez">
            We reserve the right to modify these Terms at any time. We will
            notify you of material changes by posting the updated Terms on our
            website and updating the "Last Updated" date. Your continued use of
            the Services after such changes constitutes acceptance of the
            updated Terms. If you do not agree to the modified Terms, you must
            stop using the Services.
          </p>
        </motion.div>

        {/* Contact Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-1684" data-mp="26yqt8ucgjahr">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1685" data-mp="1w6svkqi98jg3">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1686" data-mp="1r7q1u27q5znf">
            If you have questions about these Terms of Service, please contact
            us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-1687" data-mp="yjc4zpsdh9dg">
            <p data-id="element-1688" data-mp="ykn9jrtn6l5m">
              <strong data-id="element-1689" data-mp="yl5av2v5vpke">Email:</strong>{' '}
              <a href="mailto:legal@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1690" data-mp="1d8qdyws1dosb">
                legal@syneural.com
              </a>
            </p>
            <p data-id="element-1691" data-mp="kr49008wxmft">
              <strong data-id="element-1692" data-mp="kqmfhbdmxmbl">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1693" data-mp="ft2r3in83t5x">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}